import React, { useState } from 'react';
import { createPortal } from 'react-dom';

export interface ConfirmDeleteModalProps {
  /** Controla visibilidade do modal. */
  isOpen: boolean;
  /** Título exibido no topo (ex.: "Excluir Setor"). */
  title: string;
  /** Texto ou conteúdo explicando a ação irreversível. */
  message: React.ReactNode;
  /** Rótulo do botão de confirmação. */
  confirmLabel?: string;
  /** Mensagem de erro exibida abaixo do texto principal. */
  errorMessage?: string | null;
  /** Fecha o modal sem excluir. */
  onCancel: () => void;
  /** Executa a exclusão após confirmação; retorna false para manter o modal aberto. */
  onConfirm: () => boolean | Promise<boolean>;
}

/**
 * Modal padrão de confirmação de exclusão — renderizado via portal no body.
 * Evita problemas de z-index/stacking dentro de outros modais.
 */
export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmar Exclusão',
  errorMessage = null,
  onCancel,
  onConfirm,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!isOpen) return null;

  /** Dispara a exclusão e só fecha o modal se o handler retornar true. */
  const handleConfirm = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    try {
      const shouldClose = await onConfirm();
      if (!shouldClose) return;
      onCancel();
    } finally {
      setIsConfirming(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-[4px] animate-in fade-in duration-200"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden p-6 flex flex-col gap-5 text-center transform transition-all scale-100 animate-in zoom-in-95"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="w-16 h-16 bg-red-50 text-status-red rounded-full flex items-center justify-center mx-auto mb-1 border-4 border-red-50 shadow-[0_4px_20px_-4px_rgba(239,68,68,0.3)]">
          <span className="material-symbols-outlined !text-[32px]">delete_forever</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-800 mb-2 tracking-tight">{title}</h3>
          <p className="text-[13px] text-slate-500 font-medium leading-relaxed px-2">{message}</p>
          {errorMessage && (
            <p className="mt-3 text-[12px] font-semibold text-status-red bg-red-50 rounded-lg px-3 py-2">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
          <button
            type="button"
            disabled={isConfirming}
            onClick={onCancel}
            className="flex-1 py-2.5 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-all active:scale-95 shadow-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={isConfirming}
            onClick={() => void handleConfirm()}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-status-red hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/30 border border-transparent rounded-lg active:scale-95 transition-all disabled:opacity-50"
          >
            {isConfirming ? 'Excluindo...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
